import React, { lazy, Suspense } from "react";
import { QueryClientProvider } from "react-query";
import { Provider, useSelector } from "react-redux";
import { BrowserRouter, Navigate, useLocation, useRoutes } from "react-router-dom";
import Loading from "@components/common/Loading";
import ScrollToTop from "@components/common/ScrollToTop";
import { ErrorBoundary } from "@components/ErrorBoundary";
import { PATHS } from "@config";
import { NEW_MESSAGE_TYPES } from "@config/constants";
import queryClient from "@config/queryClient";
import { LoadingProvider } from "@contexts/LoadingContext";
import LayoutAuth from "@layouts/LayoutAuth";
import LayoutJobs from "@pages/JobList/components/LayoutJobs";
import store from "@redux";

const Home = lazy(() => import("../pages/Home"));
const JobDetail = lazy(() => import("../pages/JobDetail"));
const NewMessageDetail = lazy(() => import("../pages/NewMessageDetail"));
const Settings = lazy(() => import("../pages/Settings"));

const JobApply = lazy(() => import("../pages/JobList/JobApply"));
const JobApplyDetail = lazy(() => import("../pages/JobList/JobApply/JobApplyDetail"));
const JobPlan = lazy(() => import("../pages/JobList/JobPlan"));
const JobPlanDetail = lazy(() => import("../pages/JobList/JobPlan/JobPlanDetail"));
const JobWork = lazy(() => import("../pages/JobList/JobWork"));
const JobWorkDetail = lazy(() => import("../pages/JobList/JobWork/JobWorkDetail"));
const JobResultEmployment = lazy(() => import("../pages/JobList/JobResult/Employment"));
const JobResultSalary = lazy(() => import("../pages/JobList/JobResult/Salary"));
const JobResultSalaryDetail = lazy(() => import("../pages/JobList/JobResult/Salary/SalaryDetail"));
const JobContract = lazy(() => import("../pages/JobList/JobContract"));
const MakingContract = lazy(() => import("../pages/MakingContract"));
const JobWorkEndReport = lazy(() => import("../pages/JobList/JobWorkEndReport"));

const Login = lazy(() => import("../pages/Login"));
const Register = lazy(() => import("../pages/Register"));
const RegisterAgree = lazy(() => import("../pages/Register/RegisterAgree"));
const Foreigner = lazy(() => import("../pages/Foreigner"));
const Younger = lazy(() => import("../pages/Younger"));
const OfficialRegister = lazy(() => import("../pages/Register/OfficialRegister"));
const Appointment = lazy(() => import("../pages/Register/Appointment"));
const AppointmentForeign = lazy(() => import("../pages/Register/Foreigner"));
const OfficialRegistForeigner = lazy(() => import("../pages/Register/OfficialRegistForeigner"));

const ResetPassword = lazy(() => import("../pages/Resetpassword"));
const Tutorial1 = lazy(() => import("../pages/Tutorial/Tutorial1"));
const Tutorial2 = lazy(() => import("../pages/Tutorial/Tutorial2"));
const Tutorial3 = lazy(() => import("../pages/Tutorial/Tutorial3"));
const Tutorial4 = lazy(() => import("../pages/Tutorial/Tutorial4"));
const SendMailSuccess = lazy(() => import("../pages/SendMailSuccess"));
const NewPassword = lazy(() => import("../pages/Resetpassword/NewPassword"));
const NewPasswordSuccess = lazy(() => import("../pages/Resetpassword/NewPasswordSuccess"));
/* Settings */
const Proflie = lazy(() => import("../pages/Settings/Profile"));
const Password = lazy(() => import("../pages/Settings/Password"));
const ImageRegist = lazy(() => import("../pages/Settings/ImageRegist"));
const Myno = lazy(() => import("../pages/Settings/Myno"));
const Video = lazy(() => import("../pages/Settings/Video"));
const UserGuide = lazy(() => import("../pages/Settings/UserGuide"));
const Faq = lazy(() => import("../pages/Settings/Faq"));
const Contact = lazy(() => import("../pages/Settings/Contact"));
const SumajobTerm = lazy(() => import("../pages/Settings/SumajobTerm"));
const PrivacyPolicy = lazy(() => import("../pages/Settings/Policy/PrivacyPoicy"));
const AppPolicy = lazy(() => import("../pages/Settings/Policy/AppPolicy"));
const Principle = lazy(() => import("../pages/Settings/Principle"));
const Salary = lazy(() => import("../pages/Settings/Salary"));
const AboutTransfer = lazy(() => import("../pages/Settings/AboutTransfer"));
const AboutJob = lazy(() => import("../pages/Settings/AboutJob"));
const Company = lazy(() => import("../pages/Settings/Company"));
const UploadDocument = lazy(() => import("../pages/Settings/UploadDocument"));
const MyCar = lazy(() => import("../pages/Settings/MyCar"));
const Experience = lazy(() => import("../pages/Settings/Experience"));
const Notification = lazy(() => import("../pages/Settings/Notification"));
const StaffPointHistory = lazy(() => import("../pages/Settings/StaffPoint/StaffPointHistory"));
const ShiftStaffPoint = lazy(() => import("../pages/Settings/ShiftStaffPoint"));
const ShiftStaffPointDetail = lazy(() =>
  import("../pages/Settings/ShiftStaffPoint/ShiftStaffPointDetail")
);
const StaffPointExchange = lazy(() => import("../pages/Settings/StaffPointExchange"));
const PointExchangeInformation = lazy(() =>
  import("../pages/Settings/StaffPointExchange/PointExchangeInformation")
);

const NoCorrespond = lazy(() => import("../pages/NoCorrespond"));
const SearchMap = lazy(() => import("../pages/SearchMap"));
const WishList = lazy(() => import("../pages/WishList"));
const NewMessageGroup = lazy(() => import("../pages/NewMessageGroup"));
const Redirect = lazy(() => import("../pages/Redirect"));
const EntryPointTerms = lazy(() => import("../pages/EntryPointTerms"));

const Pages = () => {
  const { pathname } = useLocation();
  const { isAuth } = useSelector((state) => state.auth);

  const checkAuth =
    isAuth ||
    [PATHS.WISH_LIST, PATHS.JOB_APPLY, PATHS.NEW_MESSAGE, PATHS.SETTING].includes(pathname) ? (
      <LayoutAuth />
    ) : (
      <Navigate to={PATHS.LOGIN} replace />
    );

  return useRoutes([
    {
      element: <LayoutAuth />,
      children: [
        { path: PATHS.LOGIN, element: <Login /> },
        { path: PATHS.REGISTER, element: <Register /> },
        { path: PATHS.REGIST_AGREE1, element: <RegisterAgree /> },
        { path: PATHS.FOREINGNER, element: <Foreigner /> },
        { path: PATHS.YOUNGER, element: <Younger /> },
        { path: PATHS.TUTORIAL_1, element: <Tutorial1 /> },
        { path: PATHS.TUTORIAL_2, element: <Tutorial2 /> },
        { path: PATHS.TUTORIAL_3, element: <Tutorial3 /> },
        { path: PATHS.TUTORIAL_4, element: <Tutorial4 /> },
        { path: PATHS.RESETPASSWORD, element: <ResetPassword /> },
        { path: PATHS.SEND_MAIL_SUCCESS, element: <SendMailSuccess /> },
        { path: PATHS.NEW_PASSWORD, element: <NewPassword /> },
        { path: PATHS.NEW_PASSWORD_SUCCESS, element: <NewPasswordSuccess /> },
        { path: PATHS.HOME, element: <Home /> },
        { path: `${PATHS.JOB_DETAIL}/:id/:shiftPatternId`, element: <JobDetail /> },
      ],
    },
    {
      element: checkAuth,
      children: [
        { path: PATHS.REGIST_1, element: <OfficialRegister /> },
        { path: PATHS.REGIST_3, element: <Appointment /> },
        { path: PATHS.REGIST_FOREIGNER, element: <AppointmentForeign /> },
        { path: PATHS.OFFICIAL_REGIST_FOREIGNER, element: <OfficialRegistForeigner /> },
        { path: PATHS.SEARCH_MAP, element: <SearchMap /> },
        { path: PATHS.ENTRY_POINT_TERMS, element: <EntryPointTerms /> },
        {
          path: `${PATHS.NEWS_DETAIL}/:id`,
          element: <NewMessageDetail type={NEW_MESSAGE_TYPES.news} />,
        },
        {
          path: `${PATHS.MESSAGES_DETAIL}/:id`,
          element: <NewMessageDetail type={NEW_MESSAGE_TYPES.message} />,
        },
        { path: `${PATHS.JOB_DETAIL}/:id/:shiftPatternId`, element: <JobDetail /> },
        {
          element: <LayoutJobs />,
          children: [
            { path: PATHS.JOB_APPLY, element: <JobApply /> },
            { path: PATHS.JOB_PLAN, element: <JobPlan /> },
            { path: PATHS.JOB_WORK, element: <JobWork /> },
            { path: PATHS.JOB_RESULT_SALARY, element: <JobResultSalary /> },
            { path: PATHS.JOB_RESULT_EMPLOYMENT, element: <JobResultEmployment /> },
          ],
        },
        { path: `${PATHS.JOB_APPLY}/:id`, element: <JobApplyDetail /> },
        { path: `${PATHS.JOB_PLAN}/:id`, element: <JobPlanDetail /> },
        { path: `${PATHS.JOB_WORK}/:id`, element: <JobWorkDetail /> },
        { path: `${PATHS.JOB_RESULT_SALARY}/:id`, element: <JobResultSalaryDetail /> },
        { path: `${PATHS.JOB_WORK_END_REPORT}/:id`, element: <JobWorkEndReport /> },
        { path: PATHS.MAKING_CONTRACT, element: <MakingContract /> },
        { path: `${PATHS.JOB_CONTRACT}/:id`, element: <JobContract /> },
        { path: PATHS.PROFILE, element: <Proflie /> },
        { path: PATHS.PASSWORD, element: <Password /> },
        { path: PATHS.IMAGE_REGIST, element: <ImageRegist /> },
        { path: PATHS.MYNO, element: <Myno /> },
        { path: PATHS.VIDEO, element: <Video /> },
        { path: PATHS.USER_GUIDE, element: <UserGuide /> },
        { path: PATHS.FAQ, element: <Faq /> },
        { path: PATHS.CONTACT, element: <Contact /> },
        { path: PATHS.SUMAJOB_TERM, element: <SumajobTerm /> },
        { path: PATHS.PRIVACY_POLICY, element: <PrivacyPolicy /> },
        { path: PATHS.APP_POLICY, element: <AppPolicy /> },
        { path: PATHS.PRINCIPLE, element: <Principle /> },
        { path: PATHS.SALARY, element: <Salary /> },
        { path: PATHS.ABOUT_TRANSFER, element: <AboutTransfer /> },
        { path: PATHS.ABOUT_JOB, element: <AboutJob /> },
        { path: PATHS.COMPANY, element: <Company /> },
        { path: PATHS.UPLOAD_DOCUMENT, element: <UploadDocument /> },
        { path: PATHS.MY_CAR, element: <MyCar /> },
        { path: PATHS.EXPERIENCE, element: <Experience /> },

        { path: PATHS.NO_CORRESPOND, element: <NoCorrespond /> },
        { path: PATHS.WISH_LIST, element: <WishList /> },
        { path: PATHS.NEW_MESSAGE, element: <NewMessageGroup /> },
        { path: PATHS.SETTING, element: <Settings /> },
        { path: PATHS.SETTING_NOTIFICATION, element: <Notification /> },
        { path: PATHS.REDIRECT, element: <Redirect /> },
        { path: PATHS.STAFF_POINT_HISTORY, element: <StaffPointHistory /> },
        { path: PATHS.SHIFT_STAFF_POINT, element: <ShiftStaffPoint /> },
        { path: PATHS.SHIFT_STAFF_POINT_DETAIL, element: <ShiftStaffPointDetail /> },
        { path: PATHS.STAFF_POINT_EXCHANGE, element: <StaffPointExchange /> },
        {
          path: `${PATHS.STAFF_POINT_EXCHANGE_INFORMATION}/:id`,
          element: <PointExchangeInformation />,
        },
      ],
    },
  ]);
};

function RouterContainer() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <LoadingProvider>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <Suspense fallback={<Loading loading />}>
                <ScrollToTop />
                <Pages />
              </Suspense>
            </BrowserRouter>
          </QueryClientProvider>
        </LoadingProvider>
      </Provider>
    </ErrorBoundary>
  );
}

export default RouterContainer;
